import {Inject, Injectable, PLATFORM_ID, Signal, signal} from '@angular/core';
import {HttpClient, HttpBackend} from '@angular/common/http';
import {combineLatest, Observable} from 'rxjs';
import {Store} from '@ngrx/store';
import {AppState} from '@iw-shop/store/state/app.state';
import {EnvironmentService} from '@iw-shop/core/services/environment.service';
import {CookieService} from 'ngx-cookie-service';
import {
    ApiUserTokensDeleteRequest, ApiUserTokensDeleteResponse,
    ApiUserTokensRefreshResponse,
    ApiUserTokensSetResponse
} from "@iw-shop/api/user-tokens";
import {map, take} from "rxjs/operators";
import {isPlatformBrowser} from "@angular/common";
import {toSignal} from "@angular/core/rxjs-interop";
import {StorageState} from "@store/state/storage.state";

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    private httpWithoutInterceptor: HttpClient;

    // Wir erstellen "Signals", die den Store spiegeln.
    // Das erlaubt uns, jederzeit synchron den aktuellen Wert zu lesen.
    private readonly authState: Signal<StorageState | null> = signal<any>(null);
    private readonly isBrowser: boolean;
    constructor(
        private http: HttpClient,
        private handler: HttpBackend,
        private store: Store<AppState>,
        private environmentService: EnvironmentService,
        private cookieService: CookieService,
        @Inject(PLATFORM_ID) platformId: Object
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
        this.httpWithoutInterceptor = new HttpClient(this.handler);

        // Initialisierung: Wir abonnieren den Store einmalig, um das Signal aktuell zu halten
        this.authState = toSignal(
            this.store.select(state => state.storage),
            { initialValue: null }
        );
    }


    isShopLoginRequired$(): Observable<boolean> {
        return combineLatest([
            this.store.select(state => state.config),
            this.store.select(state => state.user)
        ]).pipe(
            // Wir wandeln die Daten in den finalen Boolean um
            map(([config, user]) => config.shopLoginRequired && !user.id),
            // Falls du nur den aktuellen Wert einmalig brauchst:
            take(1)
        );
    }

    // --- GETTER (SYNCHRON via Signals) ---

    private get storage() {
        return this.authState();
    }

    isInjectionToken(): boolean {
        return !!this.storage?.tokens?.access?.injectionToken;
    }

    getInjectionToken(): string {
        return this.storage?.tokens?.access?.injectionToken || '';
    }

    getPayloadFromInjectionToken(): { userId: string, exp: number } {
        const token = this.getInjectionToken();
        if (!token) return {userId: '', exp: 0};
        try {
            return JSON.parse(atob(token.split('.')[1]));
        } catch (e) {
            console.error("Fehler beim Dekodieren des Injection-Tokens", e);
            return {userId: '', exp: 0};
        }
    }

    getCreatedAccessTimestampInSeconds(): number {
        return this.storage?.tokens?.access?.createdTimestampInSeconds || 0;
    }

    getAccessExpiredInSeconds(): number {
        return this.storage?.tokens?.access?.expiredIn || 0;
    }

    getCreatedRefreshTimestampInSeconds(): number {
        return this.storage?.tokens?.refresh?.createdTimestampInSeconds || 0;
    }

    getExpiredRefreshInSeconds(): number {
        return this.storage?.tokens?.refresh?.expiredIn || 0;
    }

    // --- COOKIE MANAGEMENT ---

    setAccessTokenCookie() {
        if (this.isBrowser) {
            this.cookieService.set('isAccessToken', 'true', 0, '/');
        }
    }

    deleteAccessTokenCookie() {
        this.cookieService.delete('isAccessToken');
    }

    isAccessTokenCookie(): boolean {
        if (this.isBrowser) {
            return this.cookieService.get('isAccessToken') === 'true';
        }
        return false;
    }

    // --- LOGIK PRÜFUNGEN ---

    isAccessToken(): boolean {
        return !!this.storage?.tokens?.access;
    }

    isAccessTokenExpired(minutesBefore: number): boolean {
        if(!this.isAccessTokenCookie()){
            return true;
        }
        const created = this.getCreatedAccessTimestampInSeconds();
        const expires = this.getAccessExpiredInSeconds();
        if (created === 0 || expires === 0) return true;

        const nowInSeconds = Date.now() / 1000;
        // Gibt true zurück, wenn (Gültigkeit - Puffer) <= Jetzt
        return (created + expires - (minutesBefore * 60)) <= nowInSeconds;
    }



    isRefreshTokenExpired(): boolean {
        const created = this.getCreatedRefreshTimestampInSeconds();
        const expires = this.getExpiredRefreshInSeconds();
        if (created === 0 || expires === 0) return true;

        const nowInSeconds = Date.now() / 1000;
        return (created + expires) <= nowInSeconds;
    }

    // --- API CALLS ---

    login(username: string, password: string): Observable<ApiUserTokensSetResponse> {
        const body = {
            id: 'login',
            command: 'userTokens/set',
            params: {data: {username, password}}
        };
        return this.httpWithoutInterceptor.post<ApiUserTokensSetResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }


    refreshToken(): Observable<ApiUserTokensRefreshResponse> {
        const body = {id: 'refreshToken', command: 'userTokens/refresh'};
        return this.httpWithoutInterceptor.post<ApiUserTokensRefreshResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }


    logout(): Observable<ApiUserTokensDeleteResponse> {
        const body: ApiUserTokensDeleteRequest = {id: 'delete', command: 'userTokens/delete'};
        return this.httpWithoutInterceptor.post<ApiUserTokensDeleteResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

}
